import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, Truck, Building2, User, MapPin, FileText } from 'lucide-react';
import { api } from '@/api/client';

interface SignupForm {
  companyName: string;
  brandName?: string;
  ownerName?: string;
  email: string;
  mobile: string;
  password: string;
  passwordConfirm: string;
  address?: string;
  city?: string;
  state?: string;
  pincode?: string;
  gstin?: string;
  pan?: string;
  serviceType: 'B2C' | 'B2B' | 'BOTH';
  acceptedTerms: boolean;
}

export function SignupPage() {
  const [done, setDone] = useState(false);
  const { register, handleSubmit, watch, formState: { errors } } = useForm<SignupForm>({
    defaultValues: { serviceType: 'BOTH' },
  });

  const submit = useMutation({
    mutationFn: async (data: SignupForm) => {
      const { passwordConfirm, acceptedTerms, ...payload } = data;
      return (await api.post('/auth/signup/merchant', payload)).data;
    },
    onSuccess: () => { setDone(true); toast.success('Account created — check your email for the verification code'); },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Signup failed'),
  });

  const pw = watch('password');

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
        <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle2 size={48} className="mx-auto text-emerald-600 mb-3" />
          <h1 className="text-xl font-bold text-slate-900 mb-2">Account Created</h1>
          <p className="text-sm text-slate-600">
            We've emailed you a 6-digit verification code. Sign in now to confirm your email and complete
            your seller onboarding — PAN, Aadhaar, bank details, GSTIN and your pickup address. It takes
            about five minutes, and you can start using the portal as soon as it's submitted.
          </p>
          <Link to="/login" className="mt-5 inline-block px-5 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90">Sign in &amp; continue</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 to-teal-50 p-6">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl p-6 max-w-3xl w-full">
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-primary text-white p-2 rounded-lg"><Truck size={20} /></div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Create Merchant Account</h1>
            <p className="text-sm text-slate-500">Sign up to ship with ShipRoute Logistics + our courier partners</p>
          </div>
        </div>

        <form onSubmit={handleSubmit((d) => {
          if (d.password !== d.passwordConfirm) { toast.error('Passwords do not match'); return; }
          if (!d.acceptedTerms) { toast.error('Please accept terms'); return; }
          submit.mutate(d);
        })} className="space-y-4">
          <Section title="Business Details" icon={<Building2 size={14} />}>
            <Field label="Company Name *" error={errors.companyName?.message}>
              <input {...register('companyName', { required: 'Required', minLength: { value: 2, message: 'Too short' } })} className="input w-full" placeholder="Acme Trading Pvt Ltd" />
            </Field>
            <Field label="Brand Name">
              <input {...register('brandName')} className="input w-full" placeholder="Acme" />
            </Field>
            <Field label="Owner / Contact Name">
              <input {...register('ownerName')} className="input w-full" placeholder="Rajesh Kumar" />
            </Field>
            <Field label="What will you ship?">
              <select {...register('serviceType')} className="input w-full">
                <option value="B2C">B2C only (small parcels)</option>
                <option value="B2B">B2B only (heavy / bulk)</option>
                <option value="BOTH">Both B2C & B2B</option>
              </select>
            </Field>
          </Section>

          <Section title="Login Credentials" icon={<User size={14} />}>
            <Field label="Email *" error={errors.email?.message}>
              <input type="email" {...register('email', { required: 'Required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Invalid email' } })} className="input w-full" placeholder="ops@acme.in" />
            </Field>
            <Field label="Mobile *" error={errors.mobile?.message}>
              <input {...register('mobile', { required: 'Required', pattern: { value: /^[6-9]\d{9}$/, message: '10-digit Indian mobile' } })} className="input w-full font-mono" placeholder="9876543210" maxLength={10} />
            </Field>
            <Field label="Password *" error={errors.password?.message}>
              <input type="password" {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 chars' } })} className="input w-full" placeholder="••••••••" />
            </Field>
            <Field label="Confirm Password *" error={errors.passwordConfirm?.message}>
              <input type="password" {...register('passwordConfirm', { required: 'Required', validate: (v) => v === pw || 'Does not match' })} className="input w-full" placeholder="••••••••" />
            </Field>
          </Section>

          <Section title="Pickup Address (optional — you can add warehouses later)" icon={<MapPin size={14} />}>
            <Field label="Address" colSpan={2}>
              <input {...register('address')} className="input w-full" placeholder="Building, Sector, Street" />
            </Field>
            <Field label="City"><input {...register('city')} className="input w-full" placeholder="Noida" /></Field>
            <Field label="State"><input {...register('state')} className="input w-full" placeholder="Uttar Pradesh" /></Field>
            <Field label="Pincode"><input {...register('pincode', { pattern: { value: /^\d{6}$/, message: '6 digits' } })} className="input w-full font-mono" maxLength={6} /></Field>
          </Section>

          <Section title="Tax / Compliance (optional)" icon={<FileText size={14} />}>
            <Field label="GSTIN"><input {...register('gstin')} className="input w-full font-mono uppercase" placeholder="29ABCDE1234F1Z5" maxLength={15} /></Field>
            <Field label="PAN"><input {...register('pan')} className="input w-full font-mono uppercase" placeholder="ABCDE1234F" maxLength={10} /></Field>
          </Section>

          <label className="flex items-start gap-2 text-sm text-slate-700">
            <input type="checkbox" {...register('acceptedTerms')} className="mt-1 rounded" />
            <span>I agree to the <a className="text-primary underline" href="/terms" target="_blank" rel="noreferrer">Terms of Service</a> and <a className="text-primary underline" href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>. I confirm that I represent the business above.</span>
          </label>

          <button type="submit" disabled={submit.isPending}
            className="w-full px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
            {submit.isPending ? <Loader2 size={16} className="animate-spin" /> : null} Create Account
          </button>

          <div className="text-center text-sm text-slate-600">
            Already have an account? <Link to="/login" className="text-primary font-medium hover:underline">Sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">{icon} {title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function Field({ label, error, children, colSpan }: { label: string; error?: string; children: React.ReactNode; colSpan?: number }) {
  return (
    <div className={colSpan === 2 ? 'col-span-2' : ''}>
      <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
      {children}
      {error && <div className="text-xs text-rose-600 mt-0.5">{error}</div>}
    </div>
  );
}
